import {NO_SUBLEDGER_LABEL, type Subledger} from "../../SubledgerSelector";
import {
    ReportLabelKey,
    ReportRowKind,
    type PartialGlAccount,
} from "../types/types.ts";
import type {LegalEntityType} from "../../../../generated/enums.ts";

export interface LabelableRow {
    account: PartialGlAccount | null;
    kind: ReportRowKind;
    label_key: string | null;
    subledger?: Subledger | null;
    subledger_split?: boolean;
}

function subledgerLabelSuffix(row: LabelableRow): string {
    if (!row.account) return "";
    if (row.subledger) return ` — ${row.subledger.name}`;
    if (row.subledger_split) return ` — ${NO_SUBLEDGER_LABEL}`;
    return "";
}

const LABELS_BY_KEY: Record<string, string> = {
    [ReportLabelKey.Assets]: "Activa",
    [ReportLabelKey.Liabilities]: "Passiva",
    [ReportLabelKey.Revenue]: "Totale opbrengsten",
    [ReportLabelKey.CostOfSales]: "Totale inkoopkosten",
    [ReportLabelKey.PersonnelExpenses]: "Personeelskosten",
    [ReportLabelKey.OtherOperatingExpenses]: "Overige bedrijfslasten",
    [ReportLabelKey.Depreciation]: "Afschrijvingen",
    [ReportLabelKey.FinancialIncomeAndExpenses]: "Financiële baten en lasten",
    [ReportLabelKey.NonCurrentAssetsIntangible]: "Vaste activa - immaterieel",
    [ReportLabelKey.NonCurrentAssetsTangible]: "Vaste activa - materieel",
    [ReportLabelKey.NonCurrentAssetsFinancial]: "Vaste activa - financieel",
    [ReportLabelKey.CurrentAssetsInventory]: "Vlottende activa - voorraden",
    [ReportLabelKey.CurrentAssetsReceivables]: "Vlottende activa - vorderingen",
    [ReportLabelKey.CurrentAssetsSecuritiesAndCash]: "Vlottende activa - effecten & liquide middelen",
    [ReportLabelKey.Equity]: "Eigen vermogen",
    [ReportLabelKey.Provisions]: "Voorzieningen",
    [ReportLabelKey.NonCurrentLiabilities]: "Langlopende schulden",
    [ReportLabelKey.CurrentLiabilities]: "Kortlopende schulden",
    [ReportLabelKey.GrossMargin]: "Bruto marge",
    [ReportLabelKey.Ebitda]: "EBITDA - resultaat voor rente, belasting en afschrijving",
    [ReportLabelKey.Ebit]: "Bedrijfsresultaat (EBIT)",
    [ReportLabelKey.Ebt]: "Resultaat voor belasting (EBT)",
    [ReportLabelKey.BalanceTotal]: "Totaal activa / passiva",
    [ReportLabelKey.UnassignedProfitAndLoss]: "Niet toegewezen rekeningen",
    [ReportLabelKey.UnassignedBalanceSheet]: "Niet toegewezen rekeningen",
};

function isLimitedLiability(legalEntityType: LegalEntityType): boolean {
    return legalEntityType === 'limited-liability';
}

function netKpiLabel(legalEntityType: LegalEntityType): string {
    return isLimitedLiability(legalEntityType) ? "Netto Winst (BV)" : "Netto Winst (EZ / VOF)";
}

function netKpiFormula(legalEntityType: LegalEntityType): string {
    return isLimitedLiability(legalEntityType)
        ? "EBT − Vennootschapsbelasting"
        : "EBT (geen Vpb voor EZ/VOF)";
}

export function rowLabel(
    row: LabelableRow,
    legalEntityType: LegalEntityType,
): string {
    if (row.account) return row.account.label_nl + subledgerLabelSuffix(row);
    if (row.kind === ReportRowKind.Kpi && row.label_key === ReportLabelKey.NetResult) {
        return netKpiLabel(legalEntityType);
    }
    return (row.label_key && LABELS_BY_KEY[row.label_key]) ?? row.label_key ?? "";
}

export function kpiFormulaText(
    kpiKey: string,
    legalEntityType: LegalEntityType,
): string {
    switch (kpiKey) {
        case ReportLabelKey.GrossMargin:
            return "Totale opbrengsten − Totale inkoopkosten";
        case ReportLabelKey.Ebitda:
            return "Bruto marge − Personeelskosten − Overige bedrijfslasten";
        case ReportLabelKey.Ebit:
            return "EBITDA − Afschrijvingen";
        case ReportLabelKey.Ebt:
            return "EBIT + Financiële baten en lasten";
        case ReportLabelKey.NetResult:
            return netKpiFormula(legalEntityType);
        case ReportLabelKey.BalanceTotal:
            return "Σ Activa − Σ Passiva − Resultaat (balansverschil, hoort 0 te zijn)";
        default:
            return "";
    }
}
